/**
 * FindUsagesModal — search for an identifier across local Ninocat stores
 * (saved queries, scraps, history, granted forms) and live MySQL bodies
 * (routines, triggers, views, events). Results group by kind on the left,
 * with the focused match's snippet + actions on the right.
 *
 * Props:
 *   open                 boolean — controls visibility
 *   onClose              fn — close handler
 *   connectionId         number — required; routes the backend search
 *   currentDatabase      string — the query tab's active DB; default scope is
 *                                 "this database" when this is non-empty.
 *   initialToken         string — pre-fills the search box (e.g. the ident
 *                                 under the cursor when triggered via the
 *                                 editor context menu)
 *   onOpenSaved(id)      fn — host action: open this saved query in a tab
 *   onOpenScrap(id)      fn — host action: restore this scrap into a new tab
 *   onOpenHistory(sql)   fn — host action: open the history SQL in a new tab
 *   onOpenSql(sql, label) fn — generic fallback: open arbitrary SQL in a new
 *                              tab (used for routines / triggers / views /
 *                              events / forms — they don't have a dedicated
 *                              destination yet)
 */
import { createSignal, createEffect, createMemo, For, Show, onCleanup } from 'solid-js';
import Modal from './Modal.jsx';
import { findUsages, KIND_LABELS } from '../lib/findusages.js';
import { toast } from '../lib/toast.js';

const MODES = [
  { id: 'identifier', label: 'Identifier', hint: 'Quote/comment-aware. Case-insensitive. Won\'t match inside strings.' },
  { id: 'qualified',  label: 'Qualified',  hint: 'Match tbl.col specifically (not just the bare column).' },
  { id: 'exact',      label: 'Exact',      hint: 'Plain substring search — matches inside strings/comments too.' },
];

const ALL_KINDS = ['saved', 'scrap', 'history', 'form', 'routine', 'trigger', 'view', 'event'];

export default function FindUsagesModal(props) {
  const [token, setToken]   = createSignal('');
  const [mode, setMode]     = createSignal('identifier');
  const [kinds, setKinds]   = createSignal(new Set(ALL_KINDS));
  // Scope: 'this' (currentDatabase only) or 'all' (every DB on this connection).
  // Defaults to 'this' when a current DB is known — most "where is this used"
  // questions are about the schema the user is currently editing against.
  // Falls back to 'all' on first open when no DB is selected on the tab.
  const [scope, setScope]   = createSignal(props.currentDatabase ? 'this' : 'all');
  const [loading, setLoading] = createSignal(false);
  const [result, setResult] = createSignal(null);  // { matches: [...], skipped: [...] }
  const [error, setError]   = createSignal('');
  const [activeIdx, setActiveIdx] = createSignal(0);
  let inputRef;

  // Pre-fill from initialToken on open. We also auto-run the search so the
  // user sees results immediately when invoking via the editor right-click.
  createEffect(() => {
    if (props.open && props.initialToken) {
      setToken(props.initialToken);
      // Run after the createSignal updates settle.
      queueMicrotask(() => runSearch());
    }
  });

  createEffect(() => {
    if (props.open) {
      setTimeout(() => inputRef?.focus(), 30);
    } else {
      // Reset transient state when the modal closes — fresh search next time.
      setResult(null);
      setError('');
      setActiveIdx(0);
    }
  });

  const runSearch = async () => {
    const t = token().trim();
    if (!t) return;
    setLoading(true);
    setError('');
    setResult(null);
    setActiveIdx(0);
    try {
      const r = await findUsages(props.connectionId, {
        token: t, mode: mode(), kinds: [...kinds()],
        database: scope() === 'this' ? (props.currentDatabase || '') : '',
      });
      setResult(r || { matches: [], skipped: [] });
    } catch (e) {
      setError(e?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      runSearch();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const max = (result()?.matches?.length || 1) - 1;
      setActiveIdx((i) => Math.min(max, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    }
  };

  const toggleKind = (k) => {
    const s = new Set(kinds());
    if (s.has(k)) s.delete(k); else s.add(k);
    setKinds(s);
  };

  // Group matches by kind for the left rail.
  const groups = createMemo(() => {
    const r = result();
    if (!r?.matches) return [];
    const g = new Map();
    for (const m of r.matches) {
      if (!g.has(m.kind)) g.set(m.kind, []);
      g.get(m.kind).push(m);
    }
    return [...g.entries()].map(([k, ms]) => ({ kind: k, label: KIND_LABELS[k]?.plural || k, items: ms }));
  });

  const allMatches = createMemo(() => groups().flatMap((g) => g.items));
  const activeMatch = () => allMatches()[activeIdx()] || null;

  const open = (m) => {
    if (!m) return;
    if (m.kind === 'saved' && props.onOpenSaved) { props.onOpenSaved(m.id); props.onClose?.(); return; }
    if (m.kind === 'scrap' && props.onOpenScrap) { props.onOpenScrap(m.id); props.onClose?.(); return; }
    if (m.kind === 'history' && props.onOpenHistory) { props.onOpenHistory(m.sql); props.onClose?.(); return; }
    // Everything else (form / routine / trigger / view / event): open the raw
    // SQL in a new tab. We tag the tab label so the user knows what it was.
    if (props.onOpenSql) {
      const label = `${KIND_LABELS[m.kind]?.icon || ''} ${m.name}`.trim();
      props.onOpenSql(m.sql, label);
      props.onClose?.();
    } else {
      toast.error('No open-action wired for this result type.');
    }
  };

  // Highlighted snippet — splits around the match offsets so we can wrap the
  // matched text in a <mark> tag for the visual cue.
  const renderSnippet = (m) => {
    if (!m?.snippet) return null;
    const a = m.snippet.slice(0, m.matchStart);
    const b = m.snippet.slice(m.matchStart, m.matchEnd);
    const c = m.snippet.slice(m.matchEnd);
    return (
      <pre class="fu-snippet mono">
        {a}<mark>{b}</mark>{c}
      </pre>
    );
  };

  // Keyboard ESC to close — Modal handles its own close, but we need to
  // intercept Up/Down inside the input via onKey above.
  onCleanup(() => {});

  return (
    <Show when={props.open}>
      <Modal open={true} onClose={props.onClose} title="🔍 Find usages" wide>
        <div class="fu-modal-body">
        <div class="fu-toolbar">
          <input
            ref={inputRef}
            class="input fu-token mono"
            placeholder="Identifier or tbl.col …"
            value={token()}
            onInput={(e) => setToken(e.currentTarget.value)}
            onKeyDown={onKey}
          />
          <div class="fu-mode">
            <For each={MODES}>{(m) => (
              <button
                class={`btn btn-sm ${mode() === m.id ? '' : 'btn-ghost'}`}
                title={m.hint}
                onClick={() => setMode(m.id)}
              >{m.label}</button>
            )}</For>
          </div>
          <button class="btn btn-sm" disabled={!token().trim() || loading()} onClick={runSearch}>
            {loading() ? 'Searching…' : 'Search'}
          </button>
        </div>

        {/* Scope row — limit to current DB, or sweep every DB on this conn. */}
        <div class="fu-scope">
          <span class="muted" style="font-size:11px;">Scope:</span>
          <Show
            when={props.currentDatabase}
            fallback={<span class="muted" style="font-size:12px;">All databases (no current DB on this tab)</span>}
          >
            <label class="fu-scope-opt" title={`Search only within ${props.currentDatabase}. Routines / triggers / views / events filter by schema; saved queries / scraps / history / forms filter by their stored database.`}>
              <input type="radio" name="fu-scope" checked={scope() === 'this'}
                     onChange={() => setScope('this')} />
              <span>This database (<span class="mono">{props.currentDatabase}</span>)</span>
            </label>
            <label class="fu-scope-opt" title="Search across every non-system database on this connection.">
              <input type="radio" name="fu-scope" checked={scope() === 'all'}
                     onChange={() => setScope('all')} />
              <span>All databases</span>
            </label>
          </Show>
        </div>

        <div class="fu-kinds">
          <span class="muted" style="font-size:11px;">Sources:</span>
          <For each={ALL_KINDS}>{(k) => (
            <label class="fu-kind-toggle" title={KIND_LABELS[k]?.label || k}>
              <input type="checkbox" checked={kinds().has(k)} onChange={() => toggleKind(k)} />
              <span>{KIND_LABELS[k]?.icon} {KIND_LABELS[k]?.plural || k}</span>
            </label>
          )}</For>
        </div>

        <Show when={error()}>
          <div class="cr-result-err mono">{error()}</div>
        </Show>

        <div class="fu-body">
          {/* Left rail — grouped result list */}
          <div class="fu-list">
            <Show
              when={result() && allMatches().length > 0}
              fallback={
                <div class="muted" style="padding:16px;text-align:center;">
                  {loading() ? '' : (result() ? 'No matches.' : 'Type an identifier and hit Search (or Enter).')}
                </div>
              }
            >
              <For each={groups()}>{(g) => (
                <div class="fu-group">
                  <div class="fu-group-head">
                    <span>{KIND_LABELS[g.kind]?.icon} {g.label}</span>
                    <span class="muted">{g.items.length}</span>
                  </div>
                  <For each={g.items}>{(m) => {
                    const idx = allMatches().indexOf(m);
                    return (
                      <div
                        class={`fu-item ${activeIdx() === idx ? 'active' : ''}`}
                        onClick={() => setActiveIdx(idx)}
                        onDblClick={() => open(m)}
                      >
                        <div class="fu-item-title mono">
                          {m.schema ? <span class="muted">{m.schema}.</span> : null}
                          {m.name}
                        </div>
                        <Show when={m.subname}>
                          <div class="fu-item-sub muted mono">{m.subname}</div>
                        </Show>
                        <Show when={m.field}>
                          <div class="fu-item-sub muted mono">field: {m.field}</div>
                        </Show>
                      </div>
                    );
                  }}</For>
                </div>
              )}</For>
            </Show>
          </div>

          {/* Right pane — focused match's snippet + open action */}
          <div class="fu-pane">
            <Show when={activeMatch()}>
              {(() => {
                const m = activeMatch();
                return (
                  <>
                    <div class="fu-pane-head">
                      <div class="fu-pane-title mono">
                        <span class="fu-kind-pill">{KIND_LABELS[m.kind]?.icon} {KIND_LABELS[m.kind]?.label}</span>
                        {' '}
                        {m.schema ? <span class="muted">{m.schema}.</span> : null}
                        {m.name}
                        <Show when={m.subname}><span class="muted"> · {m.subname}</span></Show>
                        <Show when={m.field}><span class="muted"> · {m.field}</span></Show>
                      </div>
                      <button class="btn btn-sm" onClick={() => open(m)}>
                        Open ↗
                      </button>
                    </div>
                    {renderSnippet(m)}
                  </>
                );
              })()}
            </Show>
          </div>
        </div>

        {/* Skipped-coverage footer — honest about what wasn't searchable. */}
        <Show when={result()?.skipped?.length}>
          <div class="fu-skipped muted">
            <For each={result().skipped}>{(s) => (
              <div>
                <span class="fu-skipped-kind">{KIND_LABELS[s.kind]?.plural || s.kind}:</span>
                {' '}
                {s.count > 0 ? `${s.count} skipped — ${s.reason}` : s.reason}
              </div>
            )}</For>
          </div>
        </Show>
        </div>{/* /.fu-modal-body */}
      </Modal>
    </Show>
  );
}
